import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  ARROW_DOWN,
  ARROW_UP,
} from '@ipfabric/ui-components/utils/keyboard/constants';
import { useEffect } from 'react';
import type { MouseEvent, RefObject } from 'react';
import type Scrollbars from 'react-custom-scrollbars-2';

interface Props {
  /**
   * Ref to scroll view inside which
   */
  scrollViewRef: RefObject<Scrollbars>;
  wrapperRef: RefObject<HTMLElement>;
}

export type ArrowKeyNavigationMouseEnter = (e: MouseEvent<HTMLElement>) => void;

export interface ArrowKeyNavigationHook {
  handleOptionMouseEnter: ArrowKeyNavigationMouseEnter;
}

export const arrowKeyNavItemClass = 'arrow-key-nav-item';

const isNavItem = (element: Element | null | undefined): boolean => {
  if (!element) {
    return false;
  }

  return (
    (element.tagName === 'BUTTON' &&
      !(element as HTMLButtonElement).disabled) ||
    element.classList.contains(arrowKeyNavItemClass)
  );
};

const findNextButton = (
  element: Element | null | undefined,
): HTMLElement | null | undefined => {
  if (!element) {
    return element;
  }

  const nextElement = element.nextElementSibling;

  if (!nextElement || isNavItem(nextElement)) {
    return nextElement as HTMLElement | null | undefined;
  }
  return findNextButton(nextElement);
};

const findPreviousButton = (
  element: Element | null | undefined,
): HTMLElement | null | undefined => {
  if (!element) {
    return element;
  }

  const previousElement = element.previousElementSibling;

  if (!previousElement || isNavItem(previousElement)) {
    // Need to cast Element to HTMLElement to be able use focus.
    return previousElement as HTMLElement | null | undefined;
  }
  return findPreviousButton(previousElement);
};

/**
 * Hook to handle arrow key navigation in dropdowns.
 *
 * To be used with ScrollView (or more often with ScrollPaginator)
 *
 * Options have to be buttons OR (have [arrowKeyNavItemClass] class AND be focusable = have tabindex)
 *
 * Options should not have hover effect, but use handleOptionMouseEnter so they're focused,
 * this is important to assure there is always single highlighted element and arrow navigation
 * works as expected (when you hover element with mouse as press arrow, sibling is)
 */
export const useArrowKeyNavigation = (props: Props): ArrowKeyNavigationHook => {
  const handlePaginatorKeyDown = useStableCallback((e: KeyboardEvent) => {
    if (e.keyCode === ARROW_UP || e.keyCode === ARROW_DOWN) {
      // Disable default scrolling. Scroll position adjusts by position of focused element
      e.preventDefault();
    }

    const { activeElement } = document;

    const previousElement = findPreviousButton(activeElement);
    const nextElement = findNextButton(activeElement);

    if (e.keyCode === ARROW_UP && previousElement) {
      previousElement.focus();
    } else if (e.keyCode === ARROW_DOWN && nextElement) {
      nextElement.focus();
    }
  });

  const handleWrapperKeyDown = useStableCallback((e: KeyboardEvent) => {
    if (!props.scrollViewRef.current) {
      return;
    }
    const { container } = props.scrollViewRef.current;
    const { activeElement } = document;

    if (
      (e.keyCode === ARROW_UP || e.keyCode === ARROW_DOWN) &&
      !container.contains(activeElement)
    ) {
      e.preventDefault();
      // 1. Scrollbars contains internal div inside container so we need to go deeper
      // 2. firstChild is typed as Element | null | undefined, we need HTMLElement which has focus
      // and are quite confident it actually is HTMLElement
      const firstChild = container.firstChild?.firstChild as
        | HTMLElement
        | null
        | undefined;
      const firstButton =
        firstChild && isNavItem(firstChild)
          ? firstChild
          : findNextButton(firstChild);

      firstButton?.focus();
    }
  });

  const handleOptionMouseEnter = (e: MouseEvent<HTMLElement>) => {
    e.currentTarget.focus();
  };

  useEffect(() => {
    if (props.scrollViewRef.current) {
      const element = props.scrollViewRef.current.container;
      element.addEventListener('keydown', handlePaginatorKeyDown);

      return () => {
        element.removeEventListener('keydown', handlePaginatorKeyDown);
      };
    }

    return () => {};
  }, [props.scrollViewRef]);

  useEffect(() => {
    if (props.wrapperRef.current) {
      const element = props.wrapperRef.current;
      element.addEventListener('keydown', handleWrapperKeyDown);

      return () => {
        element.removeEventListener('keydown', handleWrapperKeyDown);
      };
    }

    return () => {};
  }, [props.wrapperRef]);

  return { handleOptionMouseEnter };
};
